import { FileUp, FolderOpen, LockKeyhole, WifiOff } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../../design-system/Button';
import type { ProcessingLimits } from '../../engine/core/types';
import { SUPPORTED_FILE_ACCEPT, unsupportedFileMessage } from '../../product/capabilities';
import { formatBytes } from '../../utilities/format';

interface FileDropzoneProps {
  onFile: (file: File) => void;
  limits: ProcessingLimits;
}

export function FileDropzone({ onFile, limits }: FileDropzoneProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const handleFiles = (files: FileList | null): void => {
    const file = files?.item(0);
    if (!file) return;
    const message = unsupportedFileMessage(file.name);
    setSelectionError(message);
    if (!message) onFile(file);
  };
  return (
    <section className="dropzone-wrap" aria-labelledby="select-file-title">
      <div
        className={`dropzone ${dragging ? 'dropzone--dragging' : ''}`}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
        onDrop={(event) => { event.preventDefault(); setDragging(false); handleFiles(event.dataTransfer.files); }}
      >
        <div className="dropzone__icon"><FileUp aria-hidden="true" /></div>
        <h2 id="select-file-title">Choose the file you want to rescue</h2>
        <p>Drop an ODT, PDF, PowerPoint, Word, Excel, or ZIP file here, or use your device’s file picker.</p>
        <Button variant="primary" onClick={() => inputRef.current?.click()}><FolderOpen />Choose a file</Button>
        <input
          ref={inputRef}
          className="visually-hidden"
          type="file"
          aria-label="Choose a file to analyze"
          accept={SUPPORTED_FILE_ACCEPT}
          onChange={(event) => {
            handleFiles(event.target.files);
            event.currentTarget.value = '';
          }}
        />
        {selectionError ? <p className="dropzone__error" role="alert">{selectionError}</p> : null}
        <p className="dropzone__limit">Active {limits.profile} safety profile · up to {formatBytes(limits.maxFileBytes)}</p>
      </div>
      <div className="trust-strip" aria-label="Privacy facts">
        <span><LockKeyhole />Private</span>
        <span><WifiOff />No upload</span>
        <span>No account</span>
        <span>Local download</span>
      </div>
    </section>
  );
}
